import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Avatar,
} from '@mui/material';
import { 
  Search, 
  PersonAdd, 
  Delete, 
  KeyboardArrowLeft,
  KeyboardArrowRight,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight
} from '@mui/icons-material';
import { COLORS_ADMIN_PAGE } from '../../conf/constants';
import { USER_SIDEBAR_CONSTANTS } from '../constants/userSidebar';
import { UserSidebarProps } from '../types/type';

interface ExtendedUserSidebarProps extends UserSidebarProps {
  onUsersPerPageChange: (usersPerPage: number) => void;
}

const UserSidebar = forwardRef<HTMLDivElement, ExtendedUserSidebarProps>(({
  sidebarWidth,
  users,
  selectedUser,
  searchTerm,
  setSearchTerm,
  currentPage,
  totalUsers,
  usersPerPage,
  setCurrentPage,
  handleUserSelect,
  handleCreateUser,
  handleDeleteUser,
  onUsersPerPageChange,
}, ref) => {
  const userListRef = useRef<HTMLDivElement>(null);
  const [calculatedUsersPerPage, setCalculatedUsersPerPage] = useState(usersPerPage);

  // Function to calculate the number of items that can be displayed
  const calculateUsersPerPage = () => {
    if (!userListRef.current) return usersPerPage;

    const containerHeight = userListRef.current.clientHeight;
    const availableHeight = containerHeight - USER_SIDEBAR_CONSTANTS.PADDING_BUFFER;
    const calculatedItems = Math.floor(availableHeight / USER_SIDEBAR_CONSTANTS.ITEM_HEIGHT) - 1;
    
    // Ensure at least MIN_ITEMS are displayed
    return Math.max(USER_SIDEBAR_CONSTANTS.MIN_ITEMS, calculatedItems);
  };

  // Hook to monitor size changes
  useEffect(() => {
    const updateUsersPerPage = () => {
      const newUsersPerPage = calculateUsersPerPage();
      if (newUsersPerPage !== calculatedUsersPerPage) {
        setCalculatedUsersPerPage(newUsersPerPage);
        onUsersPerPageChange(newUsersPerPage);
      }
    };

    // Add small delay to ensure DOM has rendered
    const timeoutId = setTimeout(updateUsersPerPage, USER_SIDEBAR_CONSTANTS.INITIAL_CALCULATION_DELAY);

    // Monitor window resize events
    const handleResize = () => {
      clearTimeout(timeoutId);
      setTimeout(updateUsersPerPage, USER_SIDEBAR_CONSTANTS.RESIZE_DEBOUNCE_DELAY);
    };

    window.addEventListener('resize', handleResize);
    
    // Monitor sidebar width changes
    const resizeObserver = new ResizeObserver(handleResize);
    if (userListRef.current) {
      resizeObserver.observe(userListRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      resizeObserver.disconnect();
    };
  }, [calculatedUsersPerPage, onUsersPerPageChange, sidebarWidth]);

  const totalPages = Math.ceil(totalUsers / usersPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleFastPageChange = (jump: number) => {
    const newPage = currentPage + jump * USER_SIDEBAR_CONSTANTS.FAST_PAGE_JUMP;
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  return (
    <Box 
      ref={ref}
      sx={{ 
        width: sidebarWidth, 
        bgcolor: COLORS_ADMIN_PAGE.WHITE, 
        borderRight: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0 
      }}
    >
      {/* Header section */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`, flexShrink: 0 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {USER_SIDEBAR_CONSTANTS.TITLE}
        </Typography>
        <TextField
          fullWidth
          placeholder={USER_SIDEBAR_CONSTANTS.SEARCH_PLACEHOLDER}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <Search sx={{ color: COLORS_ADMIN_PAGE.TEXT_SECONDARY }} />
            ),
          }}
          sx={{
            '& .MuiInputBase-input': {
              paddingY: '10px',
              paddingLeft: 1
            },
            '& .MuiOutlinedInput-root': {
              fontSize: '14px',
              borderRadius: '8px',
              paddingLeft: '8px',
            },
            '& .MuiInputAdornment-root': {
              marginRight: '8px'
            }
          }}
        />
      </Box>

      {/* Scrollable user list */}
      <Box 
        ref={userListRef}
        sx={{ 
          flex: 1,
          overflowY: 'auto', 
          bgcolor: COLORS_ADMIN_PAGE.WHITE, 
          p: 1,
          minHeight: 0 // Important: allows flex item to shrink
        }}
      >
        {users.map(user => (
          <Box
            key={user.userId}
            onClick={() => handleUserSelect(user)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              cursor: 'pointer',
              borderBottom: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
              bgcolor: selectedUser?.userId === user.userId ? COLORS_ADMIN_PAGE.PRIMARY : COLORS_ADMIN_PAGE.LIGHT_GRAY,
              color: selectedUser?.userId === user.userId ? COLORS_ADMIN_PAGE.WHITE : COLORS_ADMIN_PAGE.BLACK,
              mb: 1,
              borderRadius: '4px',
              height: `${USER_SIDEBAR_CONSTANTS.ITEM_HEIGHT}px`, // Fixed height based on constant
              boxSizing: 'border-box',
              '&:hover': {
                bgcolor: selectedUser?.userId === user.userId ? COLORS_ADMIN_PAGE.PRIMARY : COLORS_ADMIN_PAGE.BACKGROUND
              }
            }}
          >
            <Avatar src={user.profileImageUrl || ''} sx={{ width: 40, height: 40, mr: 1.5 }} />
            <Typography 
              variant="body2" 
              sx={{ 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap' // Prevent text wrapping
              }}
            >
              {user.firstName} {user.middleName} {user.lastName}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Pagination controls */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: 1, 
        borderTop: `1px solid ${COLORS_ADMIN_PAGE.BORDER}`,
        bgcolor: COLORS_ADMIN_PAGE.WHITE,
        flexShrink: 0
      }}>
        <Box>
          <Button
            variant="contained"
            onClick={() => handleFastPageChange(-1)}
            disabled={currentPage <= USER_SIDEBAR_CONSTANTS.FAST_PAGE_JUMP}
            sx={{ 
              bgcolor: COLORS_ADMIN_PAGE.PRIMARY, 
              mr: 0.5, 
              minWidth: USER_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
              px: USER_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X
            }}
          >
            <KeyboardDoubleArrowLeft />
          </Button>
          <Button
            variant="contained"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            sx={{ 
              bgcolor: COLORS_ADMIN_PAGE.PRIMARY, 
              mr: 1, 
              minWidth: USER_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
              px: USER_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X
            }}
          >
            <KeyboardArrowLeft />
          </Button>
        </Box>
        
        <Typography variant="body2" sx={{ color: COLORS_ADMIN_PAGE.TEXT_SECONDARY, mx: 1 }}>
          {currentPage} of {totalPages}
        </Typography>
        
        <Box>
          <Button
            variant="contained"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            sx={{ 
              bgcolor: COLORS_ADMIN_PAGE.PRIMARY, 
              ml: 1, 
              minWidth: USER_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
              px: USER_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X
            }}
          >
            <KeyboardArrowRight />
          </Button>
          <Button
            variant="contained"
            onClick={() => handleFastPageChange(1)}
            disabled={currentPage >= totalPages - USER_SIDEBAR_CONSTANTS.FAST_PAGE_JUMP + 1}
            sx={{ 
              bgcolor: COLORS_ADMIN_PAGE.PRIMARY, 
              ml: 0.5, 
              minWidth: USER_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.MIN_WIDTH, 
              px: USER_SIDEBAR_CONSTANTS.PAGINATION_BUTTON.PADDING_X
            }}
          >
            <KeyboardDoubleArrowRight />
          </Button>
        </Box>
      </Box>

      {/* Action buttons */}
      <Box sx={{ 
        p: 2, 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 1,
        flexShrink: 0
      }}>
        <Button
          startIcon={<PersonAdd />}
          variant="contained"
          fullWidth
          sx={{ fontWeight: 500, justifyContent: 'flex-start' }}
          onClick={handleCreateUser}
        >
          {USER_SIDEBAR_CONSTANTS.CREATE_USER_BUTTON}
        </Button>
        <Button
          startIcon={<Delete />}
          variant="contained"
          fullWidth
          sx={{ fontWeight: 500, justifyContent: 'flex-start' }}
          onClick={handleDeleteUser}
          disabled={!selectedUser}
        >
          {USER_SIDEBAR_CONSTANTS.DELETE_USER_BUTTON}
        </Button>
      </Box>
    </Box>
  );
});

UserSidebar.displayName = 'UserSidebar';

export default UserSidebar;